import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommonDropDown from "../dropdown";
import { getCompanyListAction } from "../../containers/dashboard/action";

// Multi-select dropdown of employers (companies), used by eligibility rules
// where rule_type=EMPLOYER. The wire shape for that rule is a JSON array of
// company UUIDs, so this component takes/returns `string[]`.
//
// Companies are sourced from the same companyList slot the Companies tab
// populates. We dispatch a one-shot fetch on mount with a high page_size so the
// admin sees the full set in the dropdown without paging — companies don't
// change often enough to warrant async search support yet.
function EmployerSelect({ value = [], onChange, placeholder = "Select employers", isDisabled }) {
  const dispatch = useDispatch();
  const companies = useSelector(
    (state) => state?.dashboardReducer?.companyList?.data || []
  );
  const isLoading = useSelector(
    (state) => state?.dashboardReducer?.companyList === null && state?.dashboardReducer?.loading
  );

  useEffect(() => {
    // Only fetch if we don't already have a list — keeps reopen of the form
    // from re-firing the request.
    if (!companies.length) {
      dispatch(getCompanyListAction({ page_size: 1000 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Map UUIDs the form holds → react-select option shape. Selected entries that
  // aren't (yet) in the loaded list still render with a fallback label so an
  // existing campaign with stale or pruned ids doesn't silently lose its
  // selection.
  const options = useMemo(
    () => companies.map((c) => ({ value: c.id, label: c.name || c.id })),
    [companies]
  );

  const selected = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value.map((id) => {
      const match = options.find((o) => o.value === id);
      return match || { value: id, label: id };
    });
  }, [value, options]);

  return (
    <CommonDropDown
      isMulti
      isClearable
      isDisabled={isDisabled}
      isLoading={isLoading}
      placeholder={placeholder}
      options={options}
      value={selected}
      onChange={(picked) => {
        // react-select multi gives us an array (or null when cleared). Strip
        // back down to UUIDs for the form state.
        const ids = Array.isArray(picked) ? picked.map((p) => p.value) : [];
        onChange?.(ids);
      }}
    />
  );
}

export default EmployerSelect;
